import {
	IN_CAMPAIGN_FILTER_NAME,
	IN_CAMPAIGN_FILTER_PARAM,
	inCampaignOptionLabel,
} from "@signalist/shared-types";
import { format } from "date-fns";

import { ROUTE_CONFIG } from "@/routes/config";

import { IFilterWithValues } from "../../List/components/FilterModal";
import { CustomListsAutocompleteForSequencerItem } from "../../List/components/CustomListsAutocompleteForSequencer";
import { Tabs } from "../../List/pages/Unassigned/unassignedTabs";

import { KPI_FILTER_MAP, KpiDateFilterParam, KpiKey } from "./kpiFilterMap";

// Keys here must match the `name` of the corresponding IFilter in the
// Contacts filter config (src/pages/Private/pages/Contacts/constants/contacts-filter.ts)
// so the filter chip label resolves via t(`inbox.${name}`).
const DATE_FILTER_KEY_BY_PARAM: Record<KpiDateFilterParam, string> = {
	"list_person.createdAt": "addedAt",
	"list_person.addedToCampaignAt": "addedToCampaignAt",
};

export interface BuildContactsNavigationArgs {
	kpiKey: KpiKey;
	selectedLists: CustomListsAutocompleteForSequencerItem[];
	dateRange: { from: Date | null | undefined; to: Date | null | undefined };
	segmentFilter?: IFilterWithValues | null;
	/**
	 * Extra filters already active on the analytics page (drill-down chips).
	 * Merged into the outgoing filter so the target opens with the full drilled
	 * state rather than just the clicked bucket.
	 */
	additionalFilters?: IFilterWithValues | null;
}

export interface ContactsNavigationResult {
	pathname: string;
	search: string;
}

const CONTACTS_FILTER_PARAM_KEY = "contacts_filter";
/** The list-detail leads view reads its filter from a plain `filter` param. */
const LIST_DETAIL_FILTER_PARAM_KEY = "filter";

/**
 * Every KPI except `totalLeads` filters on assign status or reply sentiment, so it
 * describes leads that are *in* a campaign. Landing those on the leads view's default
 * UNASSIGNED tab would show an empty table under a non-zero number.
 */
const LIST_DETAIL_TAB_BY_KPI: Partial<Record<KpiKey, Tabs>> = {
	totalLeads: Tabs.UNASSIGNED,
};

const listDetailTabFor = (kpiKey: KpiKey): Tabs => LIST_DETAIL_TAB_BY_KPI[kpiKey] ?? Tabs.ASSIGNED;

/** The one KPI the API counts without `list_person.campaignId IN (:campaignIds)`. */
const KPIS_WITHOUT_CAMPAIGN_SCOPE: ReadonlySet<KpiKey> = new Set(["totalLeads"]);

/**
 * The campaigns the report counted, as the leads view's own "In campaign" filter (`assignedTo`),
 * each labelled "campaign · list" like the filter's own dropdown options.
 *
 * Every campaign KPI is computed over the selection's *active* campaigns only, and a report
 * opened from a campaign on the Overview or Top Campaigns tab is scoped to that one campaign.
 * Without this the drill-down opened the whole list: "6 positive replies" for one campaign
 * landed on the 26 interested leads of all seven (PREC-4215).
 *
 * "In campaign" asks whether the person holds a confirmed assignment to one of the campaigns on
 * any list the viewer may see (PREC-4163). A campaign belongs to one list and the view shows only
 * this list's rows, so it narrows to the report's campaigns; a row only preassigned to one of
 * them (never confirmed) is no longer included.
 */
const buildCampaignFilter = (
	kpiKey: KpiKey,
	list: CustomListsAutocompleteForSequencerItem | undefined
): IFilterWithValues | null => {
	const active = (list?.campaigns ?? []).filter((campaign) => campaign.active);

	if (KPIS_WITHOUT_CAMPAIGN_SCOPE.has(kpiKey) || active.length === 0) {
		return null;
	}

	return {
		[IN_CAMPAIGN_FILTER_NAME]: {
			value: active.map((campaign) => ({
				id: campaign.campaignId,
				title: inCampaignOptionLabel(
					campaign.campaignName || campaign.campaignId,
					list?.title ?? null
				),
			})),
			type: "dropdownCustom",
			filterParam: IN_CAMPAIGN_FILTER_PARAM,
		},
	};
};

const buildListFilter = (
	selectedLists: CustomListsAutocompleteForSequencerItem[]
): IFilterWithValues | null => {
	const items = selectedLists
		.filter((list) => list.id !== null && list.id !== undefined)
		.map((list) => ({
			id: String(list.id),
			title: list.title,
		}));

	if (items.length === 0) {
		return null;
	}

	return {
		list: {
			value: items,
			type: "dropdown",
			filterParam: "list_person.listId",
		},
	};
};

const buildDateRangeFilter = (
	from: Date | null | undefined,
	to: Date | null | undefined,
	dateFilterParam: KpiDateFilterParam | null
): IFilterWithValues | null => {
	if (!from || !to || !dateFilterParam) {
		return null;
	}

	const key = DATE_FILTER_KEY_BY_PARAM[dateFilterParam];

	return {
		[key]: {
			value: {
				from: format(from, "yyyy-MM-dd"),
				till: format(to, "yyyy-MM-dd"),
			},
			type: "betweenDate",
			filterParam: dateFilterParam,
		},
	};
};

/** The one list the report is scoped to, or null when it spans none or many. */
const singleSelectedListId = (
	selectedLists: CustomListsAutocompleteForSequencerItem[]
): number | null => {
	const ids = selectedLists
		.map((list) => list.id)
		.filter((id): id is number => typeof id === "number");

	return selectedLists.length === 1 && ids.length === 1 ? ids[0] : null;
};

/**
 * Where a clicked KPI opens its leads.
 *
 * One list selected → that list's own leads view, which is the natural home for its
 * leads and keeps the list's columns and actions. More than one (or none) → Contacts,
 * the only view that can show leads from several lists at once.
 *
 * The filter travels either way, so the opened table matches the number that was
 * clicked. In the single-list case the list filter itself is dropped: the route already
 * scopes to that list, and the leads view has no list column to filter on.
 */
export const buildContactsNavigation = ({
	kpiKey,
	selectedLists,
	dateRange,
	segmentFilter,
	additionalFilters,
}: BuildContactsNavigationArgs): ContactsNavigationResult => {
	const descriptor = KPI_FILTER_MAP[kpiKey];

	const listId = singleSelectedListId(selectedLists);
	const listFilter = listId === null ? buildListFilter(selectedLists) : null;
	const dateFilter = buildDateRangeFilter(dateRange.from, dateRange.to, descriptor.dateFilterParam);

	// Merge order: descriptor base → list/date scoping → active drill-down
	// filters → the explicitly-clicked segment. The segment takes precedence so
	// same-dimension clicks override the chip (intuitive re-drill behaviour).
	const combinedFilter: IFilterWithValues = {
		...descriptor.filter,
		...(listFilter ?? {}),
		...(dateFilter ?? {}),
		...(additionalFilters ?? {}),
		...(segmentFilter ?? {}),
	};

	const params = new URLSearchParams();
	const hasFilter = Object.keys(combinedFilter).length > 0;

	if (listId !== null) {
		const listFilterWithCampaigns: IFilterWithValues = {
			...(buildCampaignFilter(kpiKey, selectedLists[0]) ?? {}),
			...combinedFilter,
		};

		params.set("tab", listDetailTabFor(kpiKey));

		if (Object.keys(listFilterWithCampaigns).length > 0) {
			params.set(LIST_DETAIL_FILTER_PARAM_KEY, JSON.stringify(listFilterWithCampaigns));
		}

		return {
			pathname: `${ROUTE_CONFIG.LISTS_DETAIL}/${listId}`,
			search: params.toString(),
		};
	}

	if (hasFilter) {
		params.set(CONTACTS_FILTER_PARAM_KEY, JSON.stringify(combinedFilter));
	}

	return {
		pathname: ROUTE_CONFIG.CONTACTS,
		search: params.toString(),
	};
};
